"use client";

import { useState } from "react";
import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import {
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { PhysicsPlot } from "@/components/tools/physics-waves/plot";
import {
  QuantityField,
  WorkingSteps,
} from "@/components/tools/physics-waves/quantity-field";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";
import {
  type NumberFormatter,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  findUnit,
  prefixUnits,
  SECOND_UNITS,
  SPEED_UNITS,
  toBase,
  type UnitDef,
} from "@/lib/tool-kit/physics-waves/units";
import {
  ABSOLUTE_ZERO_CELSIUS,
  celsiusToKelvin,
  harmonics,
  solveString,
  soundSpeedKelvin,
  soundSpeedLinear,
  type TemperatureUnit,
  toCelsius,
} from "@/lib/tool-kit/physics-waves/waves";

export interface WavePanelStrings {
  unitFor: string;
  enterAll: string;
  needPositive: string;
  working: string;
  results: string;
  figureLabel: string;
  figureDescription: string;
  wavelengthTag: string;
  stringGoalLabel: string;
  stringGoals: { v: string; tension: string; density: string };
  stringSpeed: string;
  stringTension: string;
  stringDensity: string;
  stringLength: string;
  stringLengthHint: string;
  stringStepSpeed: string;
  stringStepTension: string;
  stringStepDensity: string;
  harmonicsHeading: string;
  columnHarmonic: string;
  columnFrequency: string;
  columnWavelength: string;
  harmonicName: string;
  fundamentalName: string;
  soundTemperature: string;
  soundUnitLabel: string;
  temperatureUnits: { C: string; K: string; F: string };
  soundDelay: string;
  soundDelayHint: string;
  soundSpeed: string;
  soundSpeedKmh: string;
  soundSpeedMph: string;
  soundLinear: string;
  soundDistance: string;
  soundStep: string;
  soundStepLinear: string;
  soundBelowZero: string;
  soundGraphLabel: string;
  soundGraphDescription: string;
  soundGraphX: string;
  soundGraphY: string;
}

interface Entry {
  value: string;
  unit: string;
}

const TENSION_UNITS: UnitDef[] = [
  { id: "N", symbol: "N", exp: 0 },
  { id: "kN", symbol: "kN", exp: 3 },
  { id: "lbf", symbol: "lbf", exp: 0, factor: 4.448_221_615_260_5 },
];
const DENSITY_UNITS: UnitDef[] = [
  { id: "kg/m", symbol: "kg/m", exp: 0 },
  { id: "g/m", symbol: "g/m", exp: -3 },
  { id: "mg/m", symbol: "mg/m", exp: -6 },
];
const LENGTH_UNITS = prefixUnits("m", ["m", ""]);
const SIG_HARMONICS = 6;
const GRAPH_MIN_C = -40;
const GRAPH_MAX_C = 50;

function read(entry: Entry, units: readonly UnitDef[]): number | null {
  const parsed = parseNumberInput(entry.value);
  return parsed === null ? null : toBase(parsed, findUnit(units, entry.unit));
}

/** Two cycles of a sine wave with one wavelength marked. */
export function WaveFigure({
  wavelength,
  strings,
  fmt,
  sig,
}: {
  wavelength: number;
  strings: Pick<
    WavePanelStrings,
    "figureLabel" | "figureDescription" | "wavelengthTag"
  >;
  fmt: NumberFormatter;
  sig: number;
}) {
  const width = 560;
  const mid = 80;
  const amp = 44;
  const x0 = 40;
  const span = 240;
  const points = Array.from({ length: 121 }, (_, i) => {
    const x = x0 + (span * 2 * i) / 120;
    const y = mid - amp * Math.sin((2 * Math.PI * i) / 60);
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const label = t(strings.wavelengthTag, {
    value: withSiPrefix(fmt, wavelength, "m", sig),
  });
  return (
    <ChartSvg
      className="mx-auto block h-auto w-full max-w-xl"
      description={t(strings.figureDescription, { value: label })}
      height={170}
      id="ws-figure"
      label={strings.figureLabel}
      width={width}
    >
      <line
        stroke={CHART_INK.grid}
        strokeWidth={1}
        x1={x0}
        x2={x0 + 2 * span}
        y1={mid}
        y2={mid}
      />
      <path d={points} fill="none" stroke={CHART_COLORS[0]} strokeWidth={3} />
      <line
        stroke={CHART_INK.text}
        strokeWidth={1.5}
        x1={x0}
        x2={x0 + span}
        y1={150}
        y2={150}
      />
      <line
        stroke={CHART_INK.text}
        strokeWidth={1.5}
        x1={x0}
        x2={x0}
        y1={142}
        y2={158}
      />
      <line
        stroke={CHART_INK.text}
        strokeWidth={1.5}
        x1={x0 + span}
        x2={x0 + span}
        y1={142}
        y2={158}
      />
      <text
        fill={CHART_INK.text}
        fontSize={13}
        textAnchor="middle"
        x={x0 + span / 2}
        y={144}
      >
        {label}
      </text>
    </ChartSvg>
  );
}

const STRING_UNITS = {
  v: SPEED_UNITS,
  tension: TENSION_UNITS,
  density: DENSITY_UNITS,
};

type StringGoal = "v" | "tension" | "density";

export function StringPanel({
  strings,
  fmt,
  sig,
}: {
  strings: WavePanelStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const [goal, setGoal] = useState<StringGoal>("v");
  const [entries, setEntries] = useState<Record<StringGoal, Entry>>({
    v: { value: "418.3", unit: "m/s" },
    tension: { value: "70", unit: "N" },
    density: { value: "0.4", unit: "g/m" },
  });
  const [length, setLength] = useState<Entry>({ value: "0.65", unit: "m" });
  const set = (key: StringGoal, patch: Partial<Entry>) =>
    setEntries((current) => ({
      ...current,
      [key]: { ...current[key], ...patch },
    }));
  const tension = read(entries.tension, TENSION_UNITS);
  const density = read(entries.density, DENSITY_UNITS);
  const speed = read(entries.v, SPEED_UNITS);
  const stringLength = read(length, LENGTH_UNITS);
  const show = (value: number, unit: string) =>
    withSiPrefix(fmt, value, unit, sig);
  const inputs = (["v", "tension", "density"] as StringGoal[]).filter(
    (k) => k !== goal
  );

  const outcome = solveString(goal, {
    v: speed,
    tension,
    density,
  });
  const solved = typeof outcome === "string" ? null : outcome;
  const stepLine = (): string => {
    if (solved === null) {
      return "";
    }
    const text = {
      f: show(solved.tension, "N"),
      mu: show(solved.density, "kg/m"),
      v: show(solved.v, "m/s"),
    };
    if (goal === "v") {
      return t(strings.stringStepSpeed, text);
    }
    return t(
      goal === "tension"
        ? strings.stringStepTension
        : strings.stringStepDensity,
      text
    );
  };
  const labels = {
    v: strings.stringSpeed,
    tension: strings.stringTension,
    density: strings.stringDensity,
  };
  const rows =
    solved !== null && stringLength !== null && stringLength > 0
      ? harmonics(solved.v, stringLength, SIG_HARMONICS).map((h) => [
          h.n === 1
            ? strings.fundamentalName
            : t(strings.harmonicName, { n: h.n }),
          show(h.frequency, "Hz"),
          show(h.wavelength, "m"),
        ])
      : [];

  return (
    <>
      <PillGroup
        label={strings.stringGoalLabel}
        name="ws-string-goal"
        onChange={setGoal}
        options={[
          { value: "v", label: strings.stringGoals.v },
          { value: "tension", label: strings.stringGoals.tension },
          { value: "density", label: strings.stringGoals.density },
        ]}
        value={goal}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        {inputs.map((key) => (
          <QuantityField
            id={`ws-s-${key}`}
            key={key}
            label={labels[key]}
            onChange={(value) => set(key, { value })}
            onUnitChange={(unit) => set(key, { unit })}
            unit={entries[key].unit}
            unitLabel={t(strings.unitFor, { name: labels[key] })}
            units={STRING_UNITS[key]}
            value={entries[key].value}
          />
        ))}
        <QuantityField
          hint={strings.stringLengthHint}
          id="ws-s-length"
          label={strings.stringLength}
          onChange={(value) => setLength({ ...length, value })}
          onUnitChange={(unit) => setLength({ ...length, unit })}
          unit={length.unit}
          unitLabel={t(strings.unitFor, { name: strings.stringLength })}
          units={LENGTH_UNITS}
          value={length.value}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome === "missing" ? <Notice>{strings.enterAll}</Notice> : null}
        {outcome === "positive" ? (
          <ErrorNotice>{strings.needPositive}</ErrorNotice>
        ) : null}
        {solved === null ? null : (
          <>
            <StatGrid>
              <StatCard
                emphasis={goal === "v"}
                label={strings.stringSpeed}
                value={show(solved.v, "m/s")}
              />
              <StatCard
                emphasis={goal === "tension"}
                label={strings.stringTension}
                value={show(solved.tension, "N")}
              />
              <StatCard
                emphasis={goal === "density"}
                label={strings.stringDensity}
                value={show(solved.density, "kg/m")}
              />
            </StatGrid>
            <WorkingSteps heading={strings.working} lines={[stepLine()]} />
            {rows.length > 0 ? (
              <div className="space-y-3">
                <h3 className={WIDGET_H3}>{strings.harmonicsHeading}</h3>
                <DataTable
                  head={[
                    strings.columnHarmonic,
                    strings.columnFrequency,
                    strings.columnWavelength,
                  ]}
                  rows={rows}
                />
              </div>
            ) : null}
          </>
        )}
      </section>
    </>
  );
}

export function SoundPanel({
  strings,
  fmt,
  sig,
}: {
  strings: WavePanelStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const [temperature, setTemperature] = useState("20");
  const [unit, setUnit] = useState<TemperatureUnit>("C");
  const [delay, setDelay] = useState<Entry>({ value: "3", unit: "s" });
  const typed = parseNumberInput(temperature);
  const celsius = typed === null ? null : toCelsius(typed, unit);
  const wait = read(delay, SECOND_UNITS);
  const show = (value: number, symbol: string) =>
    withSiPrefix(fmt, value, symbol, sig);
  const plain = (value: number) =>
    fmt(value, { maximumSignificantDigits: sig, useGrouping: false });

  let body = <Notice>{strings.enterAll}</Notice>;
  if (celsius !== null && celsius <= ABSOLUTE_ZERO_CELSIUS) {
    body = <ErrorNotice>{strings.soundBelowZero}</ErrorNotice>;
  } else if (celsius !== null) {
    const kelvin = celsiusToKelvin(celsius);
    const speed = soundSpeedKelvin(kelvin);
    const linear = soundSpeedLinear(celsius);
    const curve = Array.from({ length: 46 }, (_, i) => {
      const c = GRAPH_MIN_C + ((GRAPH_MAX_C - GRAPH_MIN_C) * i) / 45;
      return [c, soundSpeedKelvin(celsiusToKelvin(c))] as const;
    });
    body = (
      <>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.soundSpeed}
            value={`${plain(speed)} m/s`}
          />
          <StatCard
            label={strings.soundSpeedKmh}
            value={`${plain(speed * 3.6)} km/h`}
          />
          <StatCard
            label={strings.soundSpeedMph}
            value={`${plain(speed / 0.447_04)} mph`}
          />
          <StatCard
            label={strings.soundLinear}
            value={`${plain(linear)} m/s`}
          />
          {wait !== null && wait >= 0 ? (
            <StatCard
              label={strings.soundDistance}
              value={show(speed * wait, "m")}
            />
          ) : null}
        </StatGrid>
        <PhysicsPlot
          curves={[{ points: curve }]}
          description={strings.soundGraphDescription}
          dots={
            celsius >= GRAPH_MIN_C && celsius <= GRAPH_MAX_C
              ? [{ x: celsius, y: speed, label: `${plain(speed)} m/s` }]
              : []
          }
          formatX={(v) => fmt(v)}
          formatY={(v) => fmt(v)}
          id="ws-sound-graph"
          label={strings.soundGraphLabel}
          xRange={[GRAPH_MIN_C, GRAPH_MAX_C]}
          xTitle={strings.soundGraphX}
          yRange={[300, 360]}
          yTitle={strings.soundGraphY}
        />
        <WorkingSteps
          heading={strings.working}
          lines={[
            t(strings.soundStep, { k: plain(kelvin), v: plain(speed) }),
            t(strings.soundStepLinear, { c: plain(celsius), v: plain(linear) }),
          ]}
        />
      </>
    );
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-2 sm:col-span-1">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="ws-temp"
          >
            {strings.soundTemperature}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card px-3 py-2 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="ws-temp"
            inputMode="decimal"
            onChange={(event) => setTemperature(event.target.value)}
            value={temperature}
          />
        </div>
        <SelectField
          id="ws-temp-unit"
          label={strings.soundUnitLabel}
          onChange={setUnit}
          options={(["C", "K", "F"] as TemperatureUnit[]).map((value) => ({
            value,
            label: strings.temperatureUnits[value],
          }))}
          value={unit}
        />
        <QuantityField
          hint={strings.soundDelayHint}
          id="ws-delay"
          label={strings.soundDelay}
          onChange={(value) => setDelay({ ...delay, value })}
          onUnitChange={(next) => setDelay({ ...delay, unit: next })}
          unit={delay.unit}
          unitLabel={t(strings.unitFor, { name: strings.soundDelay })}
          units={SECOND_UNITS}
          value={delay.value}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
